import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { getFormattedClientName } from "utils/helper";

export const generateClientBasicInfoPdf = ({
  client = {},
  dietAssessments = {},
  physicalActs = {},
  measurements = {},
  vitals = {},
}) => {
  const metrics = measurements?.metrics || {};

  const doc = new jsPDF();

  const makeTable = (title, rows, startY, newPage = false) => {
    const data = rows.filter(([k, v]) => v && v.toString().trim() !== "");
    if (data.length === 0) return startY;

    if (newPage) {
      doc.addPage();
      startY = 20;
    }

    doc.setFontSize(16);
    doc.text(title, 14, startY);

    autoTable(doc, {
      startY: startY + 6,
      head: [["Field", "Value"]],
      body: data,
      theme: "grid",
      styles: { fontSize: 11, cellPadding: 4 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });

    return doc.lastAutoTable.finalY + 10;
  };

  let y = 20;

  // --- Section 1: Client Profile (first page) ---
  y = makeTable(
    "Client Profile",
    [
      ["Name", client?.name],
      ["Phone", client?.phoneNumber],
      ["Email", client?.email],
      ["Age", client?.age],
      ["Gender", client?.gender],
      ["Occupation", client?.occupation],
      ["Food Preference", client?.foodPreference],
      ["Status", client?.status],
      ["Address", client?.address],
    ],
    y
  );

  // --- Section 2: Biochemical ---
  y = makeTable(
    "Biochemical",
    [
      ["Doctor Name", dietAssessments?.doctorName],
      ["BP", dietAssessments?.bp],
      ["Blood Sugar", dietAssessments?.bloodSugar],
      ["PP", dietAssessments?.pp],
      ["HBAIC", dietAssessments?.hbaic],
      ["Cholestrol", dietAssessments?.cholestrol],
      ["HDL", dietAssessments?.hdl],
      ["LDL", dietAssessments?.ldl],
      ["VLDL", dietAssessments?.vldl],
      ["SGOT", dietAssessments?.sgot],
      ["SGPT", dietAssessments?.sgpt],
      ["HB", dietAssessments?.hb],
      ["Vitamin D3", dietAssessments?.d3],
      ["Vitamin B12", dietAssessments?.vitaminb12],
      ["Vitamin", dietAssessments?.vitamin],
      ["Calcium", dietAssessments?.calcium],
      ["Uric Acid", dietAssessments?.uricAcid],
      ["ABG", dietAssessments?.abg],
      ["T3", dietAssessments?.t3],
      ["T4", dietAssessments?.t4],
      ["CRP", dietAssessments?.crp],
      ["Potassium", dietAssessments?.potassium],
      ["Blood Urea", dietAssessments?.bloodUrea],
      ["Blood Urea Nitrogen", dietAssessments?.bloodUreaNitrogen],
      ["Transferrin", dietAssessments?.transferrin],
      ["Triglycerides", dietAssessments?.triglycerides],
      ["Sodium", dietAssessments?.sodium],
      ["Bilirubin", dietAssessments?.bilirubin],
      ["RBC", dietAssessments?.rbc],
      ["WBC", dietAssessments?.wbc],
      ["Creatinine", dietAssessments?.creatinine],
      [
        "Last Period Date",
        dietAssessments?.lastPeriodDate
          ? moment(dietAssessments?.lastPeriodDate).format("DD-MM-YYYY")
          : "",
      ],
      [
        "Recorded At",
        dietAssessments?.timestamp
          ? moment(
              dietAssessments?.timestamp?.seconds
                ? dietAssessments?.timestamp?.seconds * 1000
                : dietAssessments?.timestamp
            ).format("MMM Do YYYY, h:mm a")
          : null,
      ],
    ],
    y,
    true
  );

  // --- Section 3: Lifestyle & Dietary ---
  y = makeTable(
    "Dietary & Lifestyle",
    [
      ["Ongoing Medication", dietAssessments?.ongoingMedication],
      ["Operated?", dietAssessments?.operated],
      ["Duration & Medication", dietAssessments?.durationMediction],
      ["Allergic From", dietAssessments?.allergicFrom],
      ["Suffering From", dietAssessments?.sufferingFrom],
      ["Any Other Condition?", dietAssessments?.anyOtherCondition],
      ["Rashes of Allergies on Skin?", dietAssessments?.rashesOnSkin],
      ["Obesity Runs in Family?", dietAssessments?.obesityInFamily],
      ["Do You Smoke?", dietAssessments?.smoke],
      ["If Yes, How many cigarettes in a day?", dietAssessments?.smokeAmount],
      ["Do You Consume Alcohol?", dietAssessments?.alcohol],
      ["How Much?", dietAssessments?.alcoholAmount],
      ["Food You Like", dietAssessments?.foodYouLike],
      ["Food You Dislike", dietAssessments?.foodYouDislike],
      ["Water consumption in a day(in Lts) (", dietAssessments?.waterIntake],
      ["How Many Hours Do You Sleep?", dietAssessments?.sleepHours],
      ["Habit of Eating Out", dietAssessments?.habitOfEating],
      ["Allergic to any food?", dietAssessments?.allergicFoods],
      ["Heartiest Meal of the Day", dietAssessments?.heartiestMeal],
      [
        "Skipped Meal of the Day",
        Array.isArray(dietAssessments?.skippedMeal)
          ? dietAssessments.skippedMeal
              .map((item) => (typeof item === "string" ? item : item.label))
              .join(", ")
          : dietAssessments?.skippedMeal || "-",
      ],
      ["Fast Food Consumption", dietAssessments?.fastFoodConsumption],
      [
        "Soft Drinks/Packed Fruit Juices Consumption",
        dietAssessments?.softDrinks,
      ],
      ["Do You Observe Fasting?", dietAssessments?.isFastingObserve],
      [
        "Earlier tried to lose weight?",
        getFormattedClientName(dietAssessments?.isLooseWeightTried),
      ],
      ["What type of programme?", dietAssessments?.looseWeightProgramme],
      ["How much weight lost? (In KG)", dietAssessments?.weightLost],
      ["How much time period? (In Months)", dietAssessments?.weightTimePeriod],
      [
        "Recorded At",
        dietAssessments?.timestamp
          ? moment(
              dietAssessments?.timestamp?.seconds
                ? dietAssessments?.timestamp?.seconds * 1000
                : dietAssessments?.timestamp
            ).format("MMM Do YYYY, h:mm a")
          : null,
      ],
    ],
    y,
    true
  );

  // --- Section 5: Physical Activities ---
  y = makeTable(
    "Physical Activities",
    [
      ["Daily Activity", physicalActs?.dailyActivity],
      ["Gym Routine", physicalActs?.gymRoutine],
      ["Physical Activities", physicalActs?.physicalActivities],
      ["Awake Late at Night?", physicalActs?.awakeLateNight],
      ["Snack at Late Night?", physicalActs?.snackAtLateNight],
      ["Do You Drink Alcohol?", physicalActs?.drinkAlcohol],
      ["Do You Smoke Cigarettes?", physicalActs?.smokeCigarettes],
      ["Do You Chew Tobacco?", physicalActs?.chewTobacco],
      ["Difficulty in Weight Loss?", physicalActs?.difficultyInWeightLoss],
      ["Weight Loss Type", physicalActs?.weightLossType],
      ["How Many Hours Do You Sleep?", physicalActs?.hourOfSleep],
      ["Lifestyle", physicalActs?.lifestyle],
      [
        "Recorded At",
        physicalActs?.timestamp
          ? moment(
              physicalActs?.timestamp?.seconds
                ? physicalActs?.timestamp?.seconds * 1000
                : physicalActs?.timestamp
            ).format("MMM Do YYYY, h:mm a")
          : null,
      ],
    ],
    y,
    true
  );

  // --- Section 6: Body Measurements ---
  let measurementRows = [
    [
      "Weight",
      metrics?.weight?.value
        ? `${metrics?.weight?.value} ${metrics?.weight?.unit}`
        : null,
    ],
    [
      "Height",
      metrics?.height?.value
        ? `${metrics?.height?.value} ${metrics?.height?.unit}`
        : null,
    ],
    [
      "Waist",
      metrics?.waist?.value
        ? `${metrics?.waist?.value} ${metrics?.waist?.unit}`
        : null,
    ],
    [
      "Hip",
      metrics?.hip?.value
        ? `${metrics?.hip?.value} ${metrics?.hip?.unit}`
        : null,
    ],
    [
      "Neck",
      metrics?.neck?.value
        ? `${metrics?.neck?.value} ${metrics?.neck?.unit}`
        : null,
    ],
    [
      "Chest",
      metrics?.chest?.value
        ? `${metrics?.chest?.value} ${metrics?.chest?.unit}`
        : null,
    ],
    [
      "Tummy",
      metrics?.tummy?.value
        ? `${metrics?.tummy?.value} ${metrics?.tummy?.unit}`
        : null,
    ],
    [
      "Arm",
      metrics?.arm?.value
        ? `${metrics?.arm?.value} ${metrics?.arm?.unit}`
        : null,
    ],
    [
      "Thigh",
      metrics?.thigh?.value
        ? `${metrics?.thigh?.value} ${metrics?.thigh?.unit}`
        : null,
    ],
    ["Consultant Notes", measurements?.consultantionNotes || null],
    ["Client Notes", measurements?.clientNotes || null],
    [
      "Recorded At",
      measurements?.timestamp
        ? moment(
            measurements?.timestamp?.seconds
              ? measurements?.timestamp?.seconds * 1000
              : measurements?.timestamp
          ).format("MMM Do YYYY, h:mm a")
        : null,
    ],
  ];

  measurementRows = measurementRows.filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );

  y = makeTable("Body Measurements", measurementRows, y, true);

  // --- Section 7: Vitals / Body Composition ---
  y = makeTable(
    "Vitals / Body Composition",
    [
      ["BMI", vitals?.bmi ? `${vitals?.bmi}` : null],
      ["Body Fat %", vitals?.fat ? `${vitals?.fat}%` : null],
      ["Visceral Fat", vitals?.vfat ? `${vitals?.vfat}` : null],
      ["Muscle Mass", vitals?.muscleMass ? `${vitals?.muscleMass}` : null],
      ["Resting Metabolism (RM)", vitals?.rm ? `${vitals?.rm}` : null],
      ["Body Age", vitals?.bodyAge ? `${vitals?.bodyAge}` : null],
      ["Consultant Notes", vitals?.consultantionNotes],
      ["Client Notes", vitals?.clientNotes],
      [
        "Recorded At",
        vitals?.timestamp
          ? moment(
              vitals?.timestamp?.seconds
                ? vitals?.timestamp?.seconds * 1000
                : vitals?.timestamp
            ).format("MMM Do YYYY, h:mm a")
          : null,
      ],
    ],
    y,
    true
  );

  doc.save(`${client?.name || "client"}_profile.pdf`);
};
